import {observer} from 'mobx-react';
import momentTimezone from 'moment-timezone';
import 'moment/locale/pt-br';
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {of} from 'rxjs';
import {mergeAll, mergeMap, tap} from 'rxjs/operators';
import styled from 'styled-components';
import {MapContext, ThemeContext} from '../../apiContexts';
import {useMap} from '../../components/map/hooks';
import useMapAlarm from '../../components/map/hooks/useMapAlarm';
import useMapDevice from '../../components/map/hooks/useMapDevice';
import useMapGeofences from '../../components/map/hooks/useMapGeofences';
import useMapLabel from '../../components/map/hooks/useMapLabel';
import useMapPosition from '../../components/map/hooks/useMapPosition';
import useMapReport from '../../components/map/hooks/useMapReport';
import useMapTrace from '../../components/map/hooks/useMapTrace';
import {initSearchData} from '../../components/search-box';
import config from '../../config';
import useDevices from '../../hooks/use-devices';
import useSocket from '../../hooks/use-socket';
import useStore from '../../hooks/use-store';
import history from '../../routes/history';
import {logOut} from '../../services/authService';
import MapRightActions from '../../views/map-right-actions';
import CenterActions from '../../views/personal/bottom/center-actions';
import '../../views/personal/client.sass';
import {BottomStyle, DevicesStyle} from '../../views/personal/styles';
import Top from '../../views/personal/top';
import FloatingReport from '../../views/reports/floating-report';
import '../../views/personal/device/device.sass';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';
import DeviceDetail from '../../views/personal/device/device-detail';
import DeviceList from '../../views/personal/device/device-list';
import useAlerts from '../../hooks/use-alerts';
import useServices from '../../hooks/use-services';
import useGeofences from '../../hooks/use-geofence';
import {useParams} from 'react-router-dom';

const Map = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
`;

let positionsCache;

const PersonalContainer = (props) => {
    const {
        location,
    } = props;

    const mapRef = useRef();
    const {
        selectedItem,
        data,
        setSelectedRow,
        selectedRow,
        setProps,
    } = useContext(MapContext);
    const {toast} = useAlerts();
    const theme = useContext(ThemeContext);
    const {positionService, driverService, commandService} = useServices();
    const {createGeofenceFromCurrentPosition} = useGeofences();
    const {store} = useStore();
    const {server, auth, events, drivers} = store;
    const {deviceId} = useParams();
    /**
     * Load devices
     */
    const {devices} = useDevices();

    const {
        positions: positionsSocket,
        events: socketEvents,
        devices: socketDevices,
    } = useSocket();

    const intl = useIntl();

    const [isOpenModalCommand, toggleModalCommand] = useState(false);
    const [commandData, setCommandData] = useState({});
    const [report, setReport] = useState();

    const query = useMemo(
        () => ({search: location.search, hash: location.hash}),
        [location.search],
    );

    const [searchData, setSearchData] = useState(initSearchData);

    const {timezone} = server.attributes;

    const {
        instance,
        trace,
        state,
        setTrace,
        toogleTrace: traceToggleCurrentDevice,
        label,
        setLabel,
        selectedMap,
        setSelectedMap,
        setReportData,
        showDevices,
        setShowDevices,
        showGeofences,
        setShowGeofences,
        follow,
        setFollow,
    } = useMap(
        {
            target: mapRef,
            center: [0, 0],
            zoom: 4,
            tiers: config.mapConfig,
            initAllDevices: true,
            cluster: {
                distance: 25,
            },
        },
        useMapPosition,
        useMapDevice,
        useMapTrace,
        useMapLabel,
        useMapReport,
        useMapGeofences,
        useMapAlarm,
    );

    useEffect(() => {
        if (query.search) {
            setReport(query.hash.slice(1));
        } else {
            setReport(null);
        }
    }, [query]);

    useEffect(() => {
        driverService.list().then((drivers) => {
            store.drivers.setDrivers(drivers);
        });
    }, []);

    useEffect(() => {
        if (devices.list.length) {
            (async () => {
                if (!positionsCache && !devices.updatedPositions) {
                    positionsCache = await positionService.list();
                    devices.updatePositions([...positionsCache]);
                }
                positionsSocket
                    .pipe(
                        tap((positions) => {
                            if (positions.length > 0) {
                                devices.updatePositions(positions);
                                positionsCache = positions;
                            }
                        }),
                        mergeAll(),
                    )
                    .subscribe((position) => {
                        devices.updatePosition(position);
                    });

                socketDevices.pipe(mergeAll()).subscribe((device) => {
                    devices.updateDevice(device);
                });

                // new Event notification
                socketEvents
                    .pipe(
                        mergeAll(),
                        mergeMap(
                            ({deviceId}) => of(devices.findById(deviceId)),
                            ({serverTime, ...e}, d) => ({
                                name: d ? d.name : '',
                                serverTime: momentTimezone(serverTime)
                                    .tz(timezone)
                                    .format(),
                                ...e,
                            }),
                        ),
                    )
                    .subscribe((event) => {
                        events.add(event);
                    });
            })();
        }
    }, [devices.list, devices.updatedPositions]);

    useEffect(() => {
        if (instance) {
            if (selectedRow) {
                instance.selectedReportPoint = selectedRow.id;
            }
        }
    }, [selectedRow]);

    useEffect(() => {
        if (instance) {
            if (selectedItem) {
                const {longitude, latitude} = selectedItem;
                if (longitude && latitude) {
                    instance.goTo([longitude, latitude], 18);
                } else {
                    instance.goTo(null, instance._instance.getView().getZoom() - 0.0001);
                }
            }
        }
    }, [selectedItem]);

    useEffect(() => {
        setReportData(data);
    }, [data]);

    useEffect(() => {
        (async () => {
            setSearchData({
                ...searchData,
                options: devices.list.map(({id, uniqueId, name}) => ({
                    id,
                    uniqueId: uniqueId,
                    name,
                })),
            });
        })();
    }, [devices.list]);

    const logoff = () => logOut();

    useEffect(() => {
        if (instance) {
            setProps({map: instance});
            const map = instance.instance;
            instance.setI18n(intl);
            setSelectedMap('osm');
            map.on('click', (event) => {
                const [feature, layer] =
                map.forEachFeatureAtPixel(event.pixel, (feature, layer) => {
                    return [feature, layer];
                }) || [];

                if (!layer) return;

                const layerName = layer.get('name');
                if (layerName === 'reportLayer') {
                    const featureName = feature.get('name');
                    if (featureName !== 'line') {
                        setSelectedRow({
                            id: feature.get('id') || feature.getId(),
                            scroll: true,
                        });
                    }
                }

                if (layerName === 'devicesLayer') {
                    const features = feature.get('features');
                    if (features.length === 1) devices.setSelected(features[0].getId());
                }
            });

            map.on('pointermove', (event) => {
                map.getTargetElement().style.cursor = map.hasFeatureAtPixel(event.pixel) ?
                    'pointer' :
                    '';
            });

            map.on('pointerdrag', (event) => {
                setFollow(false);
            });

            let zoomCache;
            map.on('movestart', (event) => {
                zoomCache = event.map.getView().getZoom();
            });

            map.on('moveend', (event) => {
                const zoom = event.map.getView().getZoom();
                zoom !== zoomCache && setFollow(false);
            });
        }
    }, [instance]);

    useEffect(() => {
        if (
            devices.updatedPositions &&
            devices.list.length &&
            state.initializedPositions
        ) {

            if (deviceId && devices.list.length) {
                devices.setSelected(deviceId);
                const {currentPosition} = devices.selectedDevice;
                if (currentPosition) {
                    const {longitude, latitude} = currentPosition;
                    instance.goTo([longitude, latitude], 18);
                }
            }
        }
    }, [instance, auth, events, deviceId, devices.list.length, state, devices.updatedPositions, setTrace, store.devices]);

    const followToogle = () => {
        const status = !follow;
        setFollow(status);
        if (status) {
            const {longitude, latitude} = devices.selectedDevice.currentPosition;
            instance.goTo([longitude, latitude]);
        }
    };

    const toggleTrace = () => {
        setTrace(!trace, devices.selectedDevice);
    };

    const traceToggleCurrent = () => {
        traceToggleCurrentDevice(devices.selectedDevice);
    };

    const toggleLabel = () => {
        setLabel(!label);
    };

    const onChangeMap = (value) => {
        setSelectedMap(value);
    };

    const toggleDevice = () => {
        setShowDevices(!showDevices);
    };

    const toggleGeofences = () => {
        setShowGeofences(!showGeofences);
    };

    const close = () => {
        instance.removeReports();
        history.push('/client');
    };

    const setDeviceSelected = (deviceId) => {
        devices.setSelected(deviceId);
        const {longitude, latitude} = devices.selectedDevice.currentPosition;
        instance.goTo([longitude, latitude], 18);
    };

    const searchDevice = useCallback((data) => {
        if (data.length) {
            const {id} = data[0];
            setDeviceSelected(id);
        }
    }, []);

    const selectDevice = (id) => {
        // devices.setSelected(id);
        // const {currentPosition} = devices.selectedDevice;
        // if (currentPosition) {
        //     const {longitude, latitude} = currentPosition;
        //     instance.goTo([longitude, latitude], 18);
        // }
        window.location.href = `/client/${id}`;
    };

    const block = (status) => {
        const {selectedDevice} = devices;
        const {id} = selectedDevice;
        const data = {deviceId: id, type: status ? 'engineStop' : 'engineResume'};
        setCommandData(data);
        toggleModalCommand(true);
    };

    const sendCommand = async () => {
        await commandService.send(commandData);
        toast.success(intl.formatMessage({id: 'commandSent'}));
        toggleModalCommand(false);
    };

    const unSelectDevice = (_) => devices.setSelected(null);

    return (
        <div id="client">
            <Map ref={mapRef}/>

            <Top
                searchData={{...searchData, onChange: searchDevice}}
                profile={auth.profile}
                logOut={logoff}
                events={events}
                setDeviceSelected={setDeviceSelected}
            />
            <div className="content"/>
            {report && (
                <FloatingReport type={report} location={location} close={close}/>
            )}

            <BottomStyle id="bottom">

                <DevicesStyle id="devices">
                    {devices.selectedDevice ? (
                        <DeviceDetail
                            selectedDevice={devices.selectedDevice}
                            server={server}
                            drivers={drivers}
                            theme={theme}
                            isOpenModalCommand={isOpenModalCommand}
                            toggleModalCommand={toggleModalCommand}
                            sendCommand={sendCommand}
                            commandData={commandData}
                            unSelectDevice={unSelectDevice}/>
                    ) : (
                        <DeviceList devices={devices} onSelect={selectDevice}/>
                    )}
                </DevicesStyle>

                {devices.selectedDevice && (
                    <CenterActions
                        trace={
                            devices.selectedDevice ? devices.selectedDevice.withTrace : false
                        }
                        traceToggle={traceToggleCurrent}
                        followToogle={followToogle}
                        follow={follow}
                        onBlock={block}
                        createGeofence={createGeofenceFromCurrentPosition}
                    />
                )}

                <MapRightActions
                    map={instance}
                    toggleTrace={toggleTrace}
                    trace={trace}
                    toggleLabel={toggleLabel}
                    label={label}
                    onChangeMap={onChangeMap}
                    selectedMap={selectedMap}
                    toggleDevice={toggleDevice}
                    toggleGeofences={toggleGeofences}
                    showGeofences={showGeofences}
                    showDevices={showDevices}
                />
            </BottomStyle>

        </div>
    );
};

export default observer(PersonalContainer);

PersonalContainer.propTypes = {
    location: PropTypes.any,
};
